import * as React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import Email from '@material-ui/icons/Email';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import { setVisible } from '@configuratorware/configurator-frontendgui/App/Reducers/UI/Actions';
import { Services } from '@configuratorware/configurator-frontendgui/App/ServiceLocator';
import { getConf } from '@configuratorware/configurator-frontendgui/App/configuration';
import { XXL_COLORS } from '../../theme/colors';

const getShareVisible = (state: { ui?: { visibilityMap?: { shareConfiguration?: boolean } } }) =>
  !!state.ui?.visibilityMap?.shareConfiguration;

const getShareWorkInProgress = (state: { ui?: { backgroundWorkMap?: { shareConfiguration?: boolean } } }) =>
  !!state.ui?.backgroundWorkMap?.shareConfiguration;

const getConfigurationCode = (state: { configurator?: { configuration?: { code?: string } } }) =>
  state.configurator?.configuration?.code ?? null;

const getPreviewImageUrl = (state: { configurator?: { configuration?: { previewImageURL?: string } } }) =>
  state.configurator?.configuration?.previewImageURL ?? null;


const IS_MOBILE = typeof navigator !== 'undefined' && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
const HAS_FILE_SHARE = IS_MOBILE && typeof navigator?.share === 'function';

const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor" aria-hidden="true">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor" aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const PinterestIcon = () => (
  <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor" aria-hidden="true">
    <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
  </svg>
);

const useStyles = makeStyles((theme) => {
  const tokens = (theme as { xxlprint?: typeof XXL_COLORS }).xxlprint || XXL_COLORS;
  return {
    paper: {
      borderRadius: 0,
      border: `2px solid ${tokens.borderStrong}`,
      backgroundColor: tokens.paperBackground,
    },
    title: {
      borderBottom: `1px solid ${tokens.borderDefault}`,
      paddingBottom: theme.spacing(1.5),
      '& h2': {
        fontSize: 16,
        fontWeight: 800,
        letterSpacing: '-0.02em',
        color: tokens.sectionTitle,
      },
    },
    content: {
      padding: theme.spacing(2.5, 3),
      minHeight: 80,
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1.5),
    },
    buttons: {
      display: 'grid',
      gridTemplateColumns: `repeat(auto-fill, minmax(100px, 1fr))`,
      gap: theme.spacing(1.5),
      width: '100%',
    },
    shareButton: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: theme.spacing(0.75),
      padding: theme.spacing(1.5, 1),
      border: `1px solid ${tokens.borderStrong}`,
      backgroundColor: tokens.paperBackground,
      cursor: 'pointer',
      color: tokens.sectionTitle,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase' as const,
      textDecoration: 'none',
      transition: 'border-color 0.12s ease, background-color 0.12s ease',
      '&:hover': {
        borderColor: tokens.accent,
        backgroundColor: tokens.tileSelectedBg,
        color: tokens.sectionTitle,
      },
    },
    instagramColor: { color: '#E1306C' },
    linkedInColor: { color: '#0A66C2' },
    pinterestColor: { color: '#E60023' },
    actions: {
      borderTop: `1px solid ${tokens.borderDefault}`,
      padding: theme.spacing(1.5, 2),
    },
  };
});

export default function SocialShareDialog() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const open = useSelector(getShareVisible);
  const saving = useSelector(getShareWorkInProgress);
  const code = useSelector(getConfigurationCode);
  const previewImageUrl = useSelector(getPreviewImageUrl);
  const shareUrl = code ? `${getConf('network.host') || window.location.origin}/code:${code}` : null;

  const handleClose = React.useCallback(() => {
    dispatch(setVisible('shareConfiguration', false));
  }, [dispatch]);

  const absolutePreviewUrl = React.useMemo(() => {
    if (!previewImageUrl) return null;
    try {
      const imageBase = getConf('network.imageURL') || window.location.origin;
      return new URL(previewImageUrl, imageBase).href;
    } catch {
      return null;
    }
  }, [previewImageUrl]);

  const prefetchedFileRef = React.useRef<File | null>(null);

  React.useEffect(() => {
    if (!open || saving || !absolutePreviewUrl) {
      prefetchedFileRef.current = null;
      return;
    }
    let cancelled = false;
    fetch(absolutePreviewUrl)
      .then((r) => r.blob())
      .then((blob) => {
        if (cancelled) return;
        const objectUrl = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => {
          URL.revokeObjectURL(objectUrl);
          if (cancelled) return;
          // 4:5 portrait canvas — optimal for Instagram feed posts
          const W = 1080, H = 1350;
          const canvas = document.createElement('canvas');
          canvas.width = W;
          canvas.height = H;
          const ctx = canvas.getContext('2d')!;
          ctx.fillStyle = XXL_COLORS.canvasBackground;
          ctx.fillRect(0, 0, W, H);
          const padding = 120;
          const scale = Math.min((W - padding * 2) / img.width, (H - padding * 2) / img.height);
          const dw = img.width * scale;
          const dh = img.height * scale;
          ctx.drawImage(img, (W - dw) / 2, (H - dh) / 2, dw, dh);
          canvas.toBlob((outBlob) => {
            if (!cancelled && outBlob) {
              prefetchedFileRef.current = new File([outBlob], 'brandnewlogo.png', { type: 'image/png' });
            }
          }, 'image/png');
        };
        img.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          if (!cancelled) {
            prefetchedFileRef.current = new File([blob], 'brandnewlogo.png', { type: 'image/png' });
          }
        };
        img.src = objectUrl;
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, saving, absolutePreviewUrl]);

  const handleInstagram = () => {
    dispatch(setVisible('shareConfiguration', false));
    const file = prefetchedFileRef.current;
    if (file) {
      navigator.share({ files: [file], url: shareUrl ?? undefined }).catch((err) => {
        if (err?.name === 'AbortError') return;
        const a = document.createElement('a');
        a.href = absolutePreviewUrl!;
        a.download = 'brandnewlogo.png';
        a.click();
      });
    } else if (absolutePreviewUrl) {
      const a = document.createElement('a');
      a.href = absolutePreviewUrl;
      a.download = 'brandnewlogo.png';
      a.click();
    }
  };

  const handleEmail = React.useCallback(async () => {
    try {
      await Services.interaction.mailConfiguration();
    } catch {
      return;
    }
    dispatch(setVisible('shareConfiguration', false));
    dispatch(setVisible('sendEmail', true));
  }, [dispatch]);

  const linkedInUrl = shareUrl
    ? `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
    : undefined;

  const pinterestUrl = absolutePreviewUrl && shareUrl
    ? `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&media=${encodeURIComponent(absolutePreviewUrl)}&description=${encodeURIComponent(t('socialShare.pinterestDescription'))}`
    : undefined;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{ className: classes.paper }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle className={classes.title} disableTypography={false}>
        {t('socialShare.title')}
      </DialogTitle>

      <DialogContent className={classes.content}>
        {saving ? (
          <CircularProgress size={24} />
        ) : (
          <div className={classes.buttons}>
            {HAS_FILE_SHARE && (
              <button type="button" className={classes.shareButton} onClick={handleInstagram}>
                <span className={classes.instagramColor}><InstagramIcon /></span>
                Instagram
              </button>
            )}

            <a
              href={linkedInUrl || '#'}
              onClick={!linkedInUrl ? (e) => e.preventDefault() : () => dispatch(setVisible('shareConfiguration', false))}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.shareButton}
              style={!linkedInUrl ? { opacity: 0.4, pointerEvents: 'none' } : undefined}
            >
              <span className={classes.linkedInColor}><LinkedInIcon /></span>
              LinkedIn
            </a>

            <a
              href={pinterestUrl || '#'}
              onClick={!pinterestUrl ? (e) => e.preventDefault() : () => dispatch(setVisible('shareConfiguration', false))}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.shareButton}
              style={!pinterestUrl ? { opacity: 0.4, pointerEvents: 'none' } : undefined}
            >
              <span className={classes.pinterestColor}><PinterestIcon /></span>
              Pinterest
            </a>

            <button type="button" className={classes.shareButton} onClick={handleEmail}>
              <Email style={{ fontSize: 28 }} />
              {t('common.email')}
            </button>
          </div>
        )}

      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button onClick={handleClose} size="small">
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
